import React, { useState, useEffect } from 'react'
import { getApiUrl, apiRequest } from '../utils/api';
import MLModels from './MLModels';

const Reports = () => {
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [activeReportTab, setActiveReportTab] = useState('summary');
  
  // Timeline state
  const [timelineData, setTimelineData] = useState([]);
  const [isTimelineLoading, setIsTimelineLoading] = useState(true);
  const [timelinePeriod, setTimelinePeriod] = useState('daily');
  const [timelineStartDate, setTimelineStartDate] = useState('');
  const [timelineEndDate, setTimelineEndDate] = useState('');

  // Team Analytics state
  const [teamPerformance, setTeamPerformance] = useState([]);
  const [individualPerformance, setIndividualPerformance] = useState([]);
  const [isTeamLoading, setIsTeamLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [teamStartDate, setTeamStartDate] = useState('');
  const [teamEndDate, setTeamEndDate] = useState('');
  const [teamPeriodType, setTeamPeriodType] = useState('weekly');
  const [teamReportType, setTeamReportType] = useState('team-wide'); // 'team-wide' or 'individual'

  // Summary Dashboard state
  const [summaryPeriodType, setSummaryPeriodType] = useState('custom');
  const [summaryStartDate, setSummaryStartDate] = useState('');
  const [summaryEndDate, setSummaryEndDate] = useState('');

  // Engagement Report state
  const [engagementData, setEngagementData] = useState([]);
  const [isEngagementLoading, setIsEngagementLoading] = useState(true);
  const [engagementPeriodType, setEngagementPeriodType] = useState('weekly');
  const [engagementStartDate, setEngagementStartDate] = useState('');
  const [engagementEndDate, setEngagementEndDate] = useState('');

  // Export state
  const [isExcelExporting, setIsExcelExporting] = useState(false);
  const [isIndividualExcelExporting, setIsIndividualExcelExporting] = useState(false);

  // Individual Campaign Details state
  const [individualCampaigns, setIndividualCampaigns] = useState([]);
  const [individualSummary, setIndividualSummary] = useState({});
  const [isIndividualLoading, setIsIndividualLoading] = useState(false);
  const [selectedTeamMember, setSelectedTeamMember] = useState('');
  const [individualStartDate, setIndividualStartDate] = useState('');
  const [individualEndDate, setIndividualEndDate] = useState('');
  const [individualPeriodType, setIndividualPeriodType] = useState('weekly');

  useEffect(() => {
    fetchReportData();
    fetchTimelineData();
    fetchTeamPerformance();
    fetchEngagementData();
  }, []);

  useEffect(() => {
    if (activeReportTab === 'timeline') {
      fetchTimelineData();
    }
  }, [timelinePeriod, timelineStartDate, timelineEndDate]);

  useEffect(() => {
    if (activeReportTab === 'team') {
      fetchTeamPerformance();
    }
  }, [teamStartDate, teamEndDate, teamPeriodType, teamReportType]);

  useEffect(() => {
    if (activeReportTab === 'summary') {
      fetchReportData();
    }
  }, [summaryStartDate, summaryEndDate, summaryPeriodType]);

  useEffect(() => {
    if (activeReportTab === 'engagement') {
      fetchEngagementData();
    }
  }, [engagementStartDate, engagementEndDate, engagementPeriodType]);


  const fetchReportData = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (summaryPeriodType !== 'custom') {
        params.append('period_type', summaryPeriodType);
      }
      
      if (summaryStartDate) params.append('start_date', summaryStartDate);
      if (summaryEndDate) params.append('end_date', summaryEndDate);
      
      const response = await apiRequest(`/reports/summary?${params}`);
      const data = await response.json();
      if (data.success) {
        setReportData(data);
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTimelineData = async () => {
    setIsTimelineLoading(true);
    try {
      const params = new URLSearchParams({
        period: timelinePeriod
      });
      
      if (timelineStartDate) params.append('start_date', timelineStartDate);
      if (timelineEndDate) params.append('end_date', timelineEndDate);
      
      const response = await apiRequest(`/campaigns/timeline?${params}`);
      const data = await response.json();
      if (data.success) {
        setTimelineData(data.timeline_data);
      }
    } catch (error) {
      console.error('Error fetching timeline data:', error);
    } finally {
      setIsTimelineLoading(false);
    }
  };

  const fetchTeamPerformance = async () => {
    setIsTeamLoading(true);
    try {
      const params = new URLSearchParams();
      
      params.append('period_type', teamPeriodType);
      params.append('report_type', teamReportType);
      
      if (teamStartDate) params.append('start_date', teamStartDate);
      if (teamEndDate) params.append('end_date', teamEndDate);
      
      const response = await apiRequest(`/team-members/performance?${params}`);
      const data = await response.json();
      if (data.success) {
        setTeamPerformance(data.team_performance || []);
        setIndividualPerformance(data.individual_performance || data.team_performance || []);
      }
    } catch (error) {
      console.error('Error fetching team performance:', error);
    } finally {
      setIsTeamLoading(false);
    }
  };

  const fetchEngagementData = async () => {
    setIsEngagementLoading(true);
    try {
      const params = new URLSearchParams();
      
      params.append('period_type', engagementPeriodType);
      
      if (engagementPeriodType === 'custom') {
        if (engagementStartDate) params.append('start_date', engagementStartDate);
        if (engagementEndDate) params.append('end_date', engagementEndDate);
      }
      
      const response = await apiRequest(`/reports/engagement?${params}`);
      const data = await response.json();
      if (data.success) {
        setEngagementData(data.engagement_data || []);
      }
    } catch (error) {
      console.error('Error fetching engagement data:', error);
    } finally {
      setIsEngagementLoading(false);
    }
  };

  const fetchIndividualCampaigns = async () => {
    if (!selectedTeamMember) return;
    
    setIsIndividualLoading(true);
    try {
      const params = new URLSearchParams();
      
      params.append('team_member', selectedTeamMember);
      params.append('period_type', individualPeriodType);
      
      if (individualPeriodType === 'custom') {
        if (individualStartDate) params.append('start_date', individualStartDate);
        if (individualEndDate) params.append('end_date', individualEndDate);
      }
      
      const response = await apiRequest(`/team-members/individual-campaigns?${params}`);
      const data = await response.json();
      if (data.success) {
        setIndividualCampaigns(data.campaigns || []);
        setIndividualSummary(data.summary || {});
      }
    } catch (error) {
      console.error('Error fetching individual campaigns:', error);
    } finally {
      setIsIndividualLoading(false);
    }
  };

  const buildExportConfig = () => {
    const params = new URLSearchParams();
    const today = new Date().toISOString().split('T')[0];
    const activeModule = activeReportTab || 'summary';
    params.append('module', activeModule);
    let filename = `reports-${activeModule}-${today}.xlsx`;

    switch (activeModule) {
      case 'summary':
        filename = `summary-report-${today}.xlsx`;
        if (summaryPeriodType) params.append('period_type', summaryPeriodType);
        if (summaryStartDate) params.append('start_date', summaryStartDate);
        if (summaryEndDate) params.append('end_date', summaryEndDate);
        break;
      case 'timeline':
        filename = `timeline-report-${today}.xlsx`;
        if (timelinePeriod) params.append('period_type', timelinePeriod);
        if (timelineStartDate) params.append('start_date', timelineStartDate);
        if (timelineEndDate) params.append('end_date', timelineEndDate);
        break;
      case 'team':
        filename = `team-analytics-${today}.xlsx`;
        if (teamReportType) params.append('report_type', teamReportType);
        if (teamPeriodType) params.append('period_type', teamPeriodType);
        if (teamStartDate) params.append('start_date', teamStartDate);
        if (teamEndDate) params.append('end_date', teamEndDate);
        break;
      case 'engagement':
        filename = `engagement-report-${today}.xlsx`;
        if (engagementPeriodType) params.append('period_type', engagementPeriodType);
        if (engagementStartDate) params.append('start_date', engagementStartDate);
        if (engagementEndDate) params.append('end_date', engagementEndDate);
        break;
      default:
        filename = `campaigns-report-${today}.xlsx`;
        break;
    }

    return { params, filename };
  };

  const handleExportExcel = async () => {
    setIsExcelExporting(true);
    try {
      const { params, filename } = buildExportConfig();
      const response = await apiRequest(`/reports/excel?${params.toString()}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Error downloading Excel workbook');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error downloading Excel workbook');
    } finally {
      setIsExcelExporting(false);
    }
  };

  const handleExportIndividualExcel = async () => {
    if (!selectedTeamMember) {
      alert('Please select a team member first.');
      return;
    }
    setIsIndividualExcelExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('module', 'individual');
      params.append('team_member', selectedTeamMember);
      params.append('individual_period_type', individualPeriodType || 'weekly');
      if (individualStartDate) params.append('start_date', individualStartDate);
      if (individualEndDate) params.append('end_date', individualEndDate);
      const response = await apiRequest(`/reports/excel?${params.toString()}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        const today = new Date().toISOString().split('T')[0];
        const safeName = selectedTeamMember.replace(/\s+/g, '-').toLowerCase();
        a.download = `individual-${safeName}-${today}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Error downloading Excel workbook');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error downloading Excel workbook');
    } finally {
      setIsIndividualExcelExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const formatPercentage = (num) => {
    // num is already a percentage (0–100); avoid double-multiplying
    const val = Number.isFinite(num) ? num : 0;
    return val.toFixed(2) + '%';
  };

  // Timeline formatting functions
  const formatTimelinePeriod = (periodStr) => {
    if (timelinePeriod === 'daily') {
      return new Date(periodStr).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } else if (timelinePeriod === 'weekly') {
      const date = new Date(periodStr);
      const endDate = new Date(date);
      endDate.setDate(date.getDate() + 6);
      return `${date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - ${endDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    } else if (timelinePeriod === 'monthly') {
      const [year, month] = periodStr.split('-');
      return new Date(year, month - 1).toLocaleDateString('en-IN', {
        month: 'long',
        year: 'numeric'
      });
    }
    return periodStr;
  };

  const getTimelinePeriodLabel = () => {
    switch (timelinePeriod) {
      case 'daily': return 'Daily';
      case 'weekly': return 'Weekly';
      case 'monthly': return 'Monthly';
      default: return 'Daily';
    }
  };

  if (!reportData) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading report data...</p>
        </div>
      </div>
    );
  }

  const { summary, platform_breakdown, tier_breakdown } = reportData;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Reports & Analytics</h2>
          <p className="text-gray-600">Export and analyze your campaign data</p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={handleExportExcel}
            disabled={isExcelExporting}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {isExcelExporting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating Excel...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2z" />
                </svg>
                Export Excel
              </>
            )}
          </button>
        </div>
      </div>

      {/* Enhanced Report Tabs */}
      <div className="card overflow-hidden">
        <div className="border-b border-gray-200/50">
          <nav className="-mb-px flex space-x-1 px-6">
            <button
              onClick={() => setActiveReportTab('summary')}
              className={`group relative py-4 px-6 border-b-2 font-medium text-sm transition-all duration-200 ${
                activeReportTab === 'summary'
                  ? 'tab-active'
                  : 'tab-inactive'
              }`}
            >
              <span className="flex items-center">
                <span className="mr-2 text-base group-hover:scale-110 transition-transform duration-200">
                  📊
                </span>
                <span>Summary Reports</span>
              </span>
              {activeReportTab === 'summary' && (
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-blue-600 rounded-full animate-bounce-subtle"></div>
              )}
            </button>
            <button
              onClick={() => setActiveReportTab('timeline')}
              className={`group relative py-4 px-6 border-b-2 font-medium text-sm transition-all duration-200 ${
                activeReportTab === 'timeline'
                  ? 'tab-active'
                  : 'tab-inactive'
              }`}
            >
              <span className="flex items-center">
                <span className="mr-2 text-base group-hover:scale-110 transition-transform duration-200">
                  📅
                </span>
                <span>Timeline Progress</span>
              </span>
              {activeReportTab === 'timeline' && (
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-blue-600 rounded-full animate-bounce-subtle"></div>
              )}
            </button>
            <button
              onClick={() => setActiveReportTab('team')}
              className={`group relative py-4 px-6 border-b-2 font-medium text-sm transition-all duration-200 ${
                activeReportTab === 'team'
                  ? 'tab-active'
                  : 'tab-inactive'
              }`}
            >
              <span className="flex items-center">
                <span className="mr-2 text-base group-hover:scale-110 transition-transform duration-200">
                  👥
                </span>
                <span>Team Analytics</span>
              </span>
              {activeReportTab === 'team' && (
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-blue-600 rounded-full animate-bounce-subtle"></div>
              )}
            </button>
            <button
              onClick={() => setActiveReportTab('engagement')}
              className={`group relative py-4 px-6 border-b-2 font-medium text-sm transition-all duration-200 ${
                activeReportTab === 'engagement'
                  ? 'tab-active'
                  : 'tab-inactive'
              }`}
            >
              <span className="flex items-center">
                <span className="mr-2 text-base group-hover:scale-110 transition-transform duration-200">
                  📈
                </span>
                <span>Engagement Report</span>
              </span>
              {activeReportTab === 'engagement' && (
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-blue-600 rounded-full animate-bounce-subtle"></div>
              )}
            </button>
            <button
              onClick={() => setActiveReportTab('ml-models')}
              className={`group relative py-4 px-6 border-b-2 font-medium text-sm transition-all duration-200 ${
                activeReportTab === 'ml-models'
                  ? 'tab-active'
                  : 'tab-inactive'
              }`}
            >
              <span className="flex items-center">
                <span className="mr-2 text-base group-hover:scale-110 transition-transform duration-200">
                  🧠
                </span>
                <span>ML Models</span>
              </span>
              {activeReportTab === 'ml-models' && (
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-blue-600 rounded-full animate-bounce-subtle"></div>
              )}
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeReportTab === 'summary' && (
        <>
          {/* Time Period Controls */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time Period</label>
                <select
                  value={summaryPeriodType}
                  onChange={(e) => setSummaryPeriodType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 min-w-[200px]"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="custom">Custom Date Range</option>
                </select>
              </div>
              
              {summaryPeriodType === 'custom' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={summaryStartDate}
                      onChange={(e) => setSummaryStartDate(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={summaryEndDate}
                      onChange={(e) => setSummaryEndDate(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={fetchReportData}
                disabled={isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <svg className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {isLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>

          {/* Enhanced Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="stat-card group animate-fade-in-up" style={{ animationDelay: '0ms' }}>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-gray-600 mb-1">Total Campaigns</p>
                  <p className="text-2xl font-bold text-gray-900 group-hover:text-gray-700 transition-colors duration-200">{summary.total_campaigns}</p>
                </div>
              </div>
            </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Spend</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(summary.total_spend)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Views</p>
              <p className="text-lg font-semibold text-gray-900">{formatNumber(summary.total_views)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Engagements</p>
              <p className="text-lg font-semibold text-gray-900">{formatNumber(summary.total_engagements)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{formatPercentage(summary.avg_er)}</div>
            <div className="text-sm text-gray-500">Average ER%</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{Math.round(summary.avg_tcs)}</div>
            <div className="text-sm text-gray-500">Average TCS</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{formatCurrency(summary.avg_cpv)}</div>
            <div className="text-sm text-gray-500">Average CPV</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{formatCurrency(summary.avg_cpe)}</div>
            <div className="text-sm text-gray-500">Average CPE</div>
          </div>
        </div>
      </div>

      {/* Platform Breakdown */}
      {platform_breakdown && Object.keys(platform_breakdown).length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Platform Performance</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaigns</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Spend</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Views</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Spend</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(platform_breakdown).map(([platform, data]) => (
                  <tr key={platform}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{platform}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{data.count}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(data.spend)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(data.views)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(data.spend / data.count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tier Breakdown */}
      {tier_breakdown && Object.keys(tier_breakdown).length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Tier Performance</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaigns</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Spend</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Views</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Spend</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(tier_breakdown).map(([tier, data]) => (
                  <tr key={tier}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{tier}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{data.count}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(data.spend)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(data.views)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(data.spend / data.count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Team Breakdown */}
      {reportData.team_breakdown && Object.keys(reportData.team_breakdown).length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Team Performance</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team Member</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaigns</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Spend</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Views</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Engagements</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Influencers</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(reportData.team_breakdown).map(([member, data]) => (
                  <tr key={member}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{member}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{data.count}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(data.spend)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(data.views)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(data.engagements)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{data.influencer_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Export Options */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Export Options</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h4 className="font-medium text-gray-900">Excel Export</h4>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Download presentation-ready Excel workbooks with frozen headers, polished tables, and embedded charts.
            </p>
            <button
              onClick={handleExportExcel}
              disabled={isExcelExporting}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isExcelExporting ? 'Building Excel...' : 'Download Excel'}
            </button>
          </div>

        </div>
      </div>
        </>
      )}

      {/* Timeline Tab Content */}
      {activeReportTab === 'timeline' && (
        <>
          {/* Timeline Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center space-x-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Timeline Period</label>
                  <select
                    value={timelinePeriod}
                    onChange={(e) => setTimelinePeriod(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={timelineStartDate}
                    onChange={(e) => setTimelineStartDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={timelineEndDate}
                    onChange={(e) => setTimelineEndDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    setTimelineStartDate('');
                    setTimelineEndDate('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>

          {/* Timeline Loading */}
          {isTimelineLoading ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Loading timeline data...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Timeline Summary */}
              {timelineData.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-500">Total {getTimelinePeriodLabel()} Periods</p>
                        <p className="text-lg font-semibold text-gray-900">{timelineData.length}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-500">Total Campaigns</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {timelineData.reduce((sum, item) => sum + item.campaigns, 0)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-500">Total Views</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {formatNumber(timelineData.reduce((sum, item) => sum + item.views, 0))}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-500">Total Engagements</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {formatNumber(timelineData.reduce((sum, item) => sum + item.engagements, 0))}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Timeline Data Table */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">{getTimelinePeriodLabel()} Progress</h3>
                </div>
                
                {timelineData.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No timeline data</h3>
                    <p className="text-gray-500">Create some campaigns to see progress over time.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaigns</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Spend</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Engagements</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Avg TCS</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Avg ER%</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Influencers</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {timelineData.map((item, index) => (
                          <tr key={item.period} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {formatTimelinePeriod(item.period)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.campaigns}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(item.spend)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(item.views)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(item.engagements)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{Math.round(item.avg_tcs)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatPercentage(item.avg_er)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{item.influencer_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Engagement Breakdown */}
              {timelineData.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Engagement Breakdown</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Likes</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Comments</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Shares</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Reposts</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {timelineData.map((item) => (
                          <tr key={item.period} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {formatTimelinePeriod(item.period)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(item.likes)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(item.comments)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(item.shares)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(item.reposts)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">{formatNumber(item.engagements)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Team Analytics Tab Content */}
      {activeReportTab === 'team' && (
        <>
          {/* Team Date Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="space-y-4">
              {/* Report Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="teamReportType"
                      value="team-wide"
                      checked={teamReportType === 'team-wide'}
                      onChange={(e) => setTeamReportType(e.target.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">📊 Team-Wide Report</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="teamReportType"
                      value="individual"
                      checked={teamReportType === 'individual'}
                      onChange={(e) => setTeamReportType(e.target.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">📋 Individual Campaign Details</span>
                  </label>
                </div>
                
                {/* Report Type Description */}
                <p className="text-xs text-gray-500 mt-2">
                  {teamReportType === 'team-wide' 
                    ? "Collective team performance analysis with aggregated metrics"
                    : "Individual team member performance analysis with detailed breakdowns"
                  }
                </p>
              </div>

              {/* Period Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time Period</label>
                <select
                  value={teamPeriodType}
                  onChange={(e) => setTeamPeriodType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 min-w-[200px]"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="custom">Custom Date Range</option>
                </select>
              </div>

              {/* Date Range (only show for custom period) */}
              {teamPeriodType === 'custom' && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center space-x-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={teamStartDate}
                    onChange={(e) => setTeamStartDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={teamEndDate}
                    onChange={(e) => setTeamEndDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    setTeamStartDate('');
                    setTeamEndDate('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Clear Filters
                </button>
              </div>
                </div>
              )}
            </div>
          </div>

          {isTeamLoading ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Loading team analytics...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Team-Wide Report */}
              {teamReportType === 'team-wide' ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    📊 Team-Wide Performance Report ({teamPeriodType.charAt(0).toUpperCase() + teamPeriodType.slice(1)})
                  </h3>
                  
                  {/* Team Summary Cards */}
                  {teamPerformance.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      <div className="stat-card">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                            </div>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-500">Total Campaigns</p>
                            <p className="text-lg font-semibold text-gray-900">{teamPerformance[0].total_campaigns}</p>
                          </div>
                        </div>
                      </div>

                      <div className="stat-card">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 3M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599 1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-500">Total Spend</p>
                            <p className="text-lg font-semibold text-gray-900">{formatCurrency(teamPerformance[0].total_spend)}</p>
                          </div>
                        </div>
                      </div>

                      <div className="stat-card">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-9 0 3 3 0 019 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </div>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-500">Total Views</p>
                            <p className="text-lg font-semibold text-gray-900">{formatNumber(teamPerformance[0].total_views)}</p>
                          </div>
                        </div>
                      </div>

                      <div className="stat-card">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center align-middle">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                              </svg>
                            </div>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-500">Avg TCS Score</p>
                            <p className="text-lg font-semibold text-gray-900">{Math.round(teamPerformance[0].avg_tcs)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Detailed Team Member Breakdown */}
                  {individualPerformance.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-md font-medium text-gray-900 mb-4">📋 Team Member Breakdown</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 shadow-sm rounded-lg">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team Member</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaigns</th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Spend</th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Views</th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Engagements</th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Avg TCS</th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Avg ER%</th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Influencers</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {individualPerformance && individualPerformance.filter(member => member && member.team_member).map((member, index) => (
                              <tr key={member.team_member} className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  <div className="flex items-center">
                                    <div className={`w-3 h-3 rounded-full mr-3 ${
                                      index < 3 ? 'bg-green-500' : index < 6 ? 'bg-yellow-500' : 'bg-gray-400'
                                    }`}></div>
                                    {member.team_member || 'Unknown'}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {member.total_campaigns || 0}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(member.total_spend || 0)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(member.total_views || 0)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(member.total_engagements || 0)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    (member.avg_tcs || 0) >= 75 ? 'bg-green-100 text-green-800' :
                                    (member.avg_tcs || 0) >= 50 ? 'bg-yellow-100 text-yellow-800' :
                                    (member.avg_tcs || 0) >= 25 ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                    {Math.round(member.avg_tcs || 0)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatPercentage(member.avg_er || 0)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                    {Object.keys(member.influencers || {}).length}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Performance Insights */}
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                          <h5 className="text-sm font-medium text-green-900 mb-2">🏆 Top Performer</h5>
                          <p className="text-lg font-semibold text-green-700">
                            {individualPerformance && individualPerformance.length > 0 ? 
                              individualPerformance.reduce((prev, current) => 
                                ((current.total_campaigns || 0) > (prev.total_campaigns || 0)) ? current : prev
                              ).team_member : 'N/A'}
                          </p>
                        </div>
                        
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                          <h5 className="text-sm font-medium text-blue-900 mb-2">📈 Best TCS Score</h5>
                          <p className="text-lg font-semibold text-blue-700">
                            {individualPerformance && individualPerformance.length > 0 ? 
                              Math.round(Math.max(...individualPerformance.map(m => m.avg_tcs || 0))) : 0}
                          </p>
                        </div>
                        
                        <div className="bg-gradient-to-r from-purple-50 to-violet-50 border border-purple-200 rounded-lg p-4">
                          <h5 className="text-sm font-medium text-purple-900 mb-2">👥 Active Members</h5>
                          <p className="text-lg font-semibold text-purple-700">
                            {individualPerformance ? individualPerformance.length : 0} members
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    👤 Individual Member Performance Report ({teamPeriodType.charAt(0).toUpperCase() + teamPeriodType.slice(1)})
                  </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team Member</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaigns</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Spend</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Views</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Engagements</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Avg TCS</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Avg ER%</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Influencers</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {teamPerformance && teamPerformance.filter(member => member && member.team_member).map((member) => (
                        <tr key={member.team_member} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedMember(selectedMember === member.team_member ? null : member.team_member)}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{member.team_member || 'Unknown'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{member.total_campaigns || 0}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(member.total_spend || 0)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(member.total_views || 0)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(member.total_engagements || 0)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{Math.round(member.avg_tcs || 0)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatPercentage(member.avg_er || 0)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{Object.keys(member.influencers || {}).length}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              )}

              {/* Individual Campaign Details */}
              {teamReportType === 'individual' && (
                <>
                  {/* Team Member Selection */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        📋 Individual Campaign Details
                      </h3>
                      
                      {/* Team Member Selection */}
                        <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Select Team Member</label>
                        <select
                          value={selectedTeamMember}
                          onChange={(e) => setSelectedTeamMember(e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 min-w-[200px]"
                        >
                          <option value="">Select a team member...</option>
                          {individualPerformance.map((member) => (
                            <option key={member.team_member} value={member.team_member}>
                              {member.team_member}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Load Button */}
                      <div>
                        <button
                          onClick={fetchIndividualCampaigns}
                          disabled={!selectedTeamMember || isIndividualLoading}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isIndividualLoading ? 'Loading...' : 'Load Campaign Details'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Individual Campaigns Content */}
                  {isIndividualLoading ? (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-500">Loading individual campaign details...</p>
                      </div>
                    </div>
                  ) : selectedTeamMember && individualCampaigns.length > 0 ? (
                    <>
                      {/* Summary Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <span className="text-blue-600 text-sm font-medium">📊</span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <p className="text-sm font-medium text-gray-500">Total Campaigns</p>
                              <p className="text-2xl font-semibold text-gray-900">{individualSummary.total_campaigns || 0}</p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                <span className="text-green-600 text-sm font-medium">💰</span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <p className="text-sm font-medium text-gray-500">Total Spend</p>
                              <p className="text-2xl font-semibold text-gray-900">₹{individualSummary.total_spend || 0}</p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                <span className="text-purple-600 text-sm font-medium">👁️</span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <p className="text-sm font-medium text-gray-500">Total Views</p>
                              <p className="text-2xl font-semibold text-gray-900">{individualSummary.total_views || 0}</p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                                <span className="text-orange-600 text-sm font-medium">💬</span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <p className="text-sm font-medium text-gray-500">Total Engagements</p>
                              <p className="text-2xl font-semibold text-gray-900">{individualSummary.total_engagements || 0}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Campaign Details Table */}
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="px-6 py-4 border-b border-gray-200 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                          <div>
                            <h3 className="text-lg font-medium text-gray-900">
                              📋 Campaign Details for {selectedTeamMember}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                              Complete campaign data with all metrics and details
                            </p>
                          </div>
                          <button
                            onClick={handleExportIndividualExcel}
                            disabled={
                              isIndividualExcelExporting ||
                              !selectedTeamMember ||
                              individualCampaigns.length === 0
                            }
                            className="inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isIndividualExcelExporting ? 'Exporting...' : 'Download Excel'}
                          </button>
                        </div>
                        
                          <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 table-fixed">
                            <thead className="bg-gray-50 compact-head">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Campaign
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Caption
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Short Code
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Post Link
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Platform
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                                  Amount
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                                  Views
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                                  Likes
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                                  Comments
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                  Reshares
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                  Shares
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  TCS
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  TCS Verdict
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  ER%
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  ER Verdict
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  CPV
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  CPV Verdict
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  CPE
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  CPE Verdict
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Cost/Comment
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Comment Verdict
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Cost/Share
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Share Verdict
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Cost/Repost
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Repost Verdict
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Video URL
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Actions
                                </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                              {individualCampaigns.map((campaign) => (
                                <tr key={campaign.id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {campaign.date}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {campaign.influencer_name}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate" title={campaign.caption || ''}>
                                    {campaign.caption || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {campaign.short_code || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                                    {campaign.post_link ? (
                                      <a 
                                        href={campaign.post_link} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-900"
                                      >
                                        🔗
                                      </a>
                                    ) : 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      {campaign.platform}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                    ₹{campaign.amount || 0}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                    {campaign.views?.toLocaleString() || 0}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                    {campaign.likes?.toLocaleString() || 0}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                    {campaign.comments?.toLocaleString() || 0}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                    {campaign.reposts?.toLocaleString() || 0}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                    {campaign.shares?.toLocaleString() || 0}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                    {campaign.tcs_score?.toFixed(1) || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      campaign.tcs_verdict?.includes('Excellent') ? 'bg-green-100 text-green-800' :
                                      campaign.tcs_verdict?.includes('Good') ? 'bg-yellow-100 text-yellow-800' :
                                      campaign.tcs_verdict?.includes('Okay') ? 'bg-orange-100 text-orange-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      {campaign.tcs_verdict || 'N/A'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                    {campaign.er_percentage?.toFixed(1) || 'N/A'}%
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      campaign.er_verdict?.includes('Excellent') ? 'bg-green-100 text-green-800' :
                                      campaign.er_verdict?.includes('Good') ? 'bg-yellow-100 text-yellow-800' :
                                      campaign.er_verdict?.includes('Okay') ? 'bg-orange-100 text-orange-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      {campaign.er_verdict || 'N/A'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                    ₹{campaign.cpv?.toFixed(2) || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      campaign.cpv_verdict?.includes('Excellent') ? 'bg-green-100 text-green-800' :
                                      campaign.cpv_verdict?.includes('Good') ? 'bg-yellow-100 text-yellow-800' :
                                      campaign.cpv_verdict?.includes('Okay') ? 'bg-orange-100 text-orange-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      {campaign.cpv_verdict || 'N/A'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                    ₹{campaign.cpe?.toFixed(2) || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      campaign.cpe_verdict?.includes('Excellent') ? 'bg-green-100 text-green-800' :
                                      campaign.cpe_verdict?.includes('Good') ? 'bg-yellow-100 text-yellow-800' :
                                      campaign.cpe_verdict?.includes('Okay') ? 'bg-orange-100 text-orange-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      {campaign.cpe_verdict || 'N/A'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                    ₹{campaign.cost_per_comment?.toFixed(2) || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      campaign.comment_verdict?.includes('Excellent') ? 'bg-green-100 text-green-800' :
                                      campaign.comment_verdict?.includes('Good') ? 'bg-yellow-100 text-yellow-800' :
                                      campaign.comment_verdict?.includes('Okay') ? 'bg-orange-100 text-orange-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      {campaign.comment_verdict || 'N/A'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                    ₹{campaign.cost_per_share?.toFixed(2) || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      campaign.share_verdict?.includes('Excellent') ? 'bg-green-100 text-green-800' :
                                      campaign.share_verdict?.includes('Good') ? 'bg-yellow-100 text-yellow-800' :
                                      campaign.share_verdict?.includes('Okay') ? 'bg-orange-100 text-orange-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      {campaign.share_verdict || 'N/A'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                    ₹{campaign.cost_per_repost?.toFixed(2) || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      campaign.repost_verdict?.includes('Excellent') ? 'bg-green-100 text-green-800' :
                                      campaign.repost_verdict?.includes('Good') ? 'bg-yellow-100 text-yellow-800' :
                                      campaign.repost_verdict?.includes('Okay') ? 'bg-orange-100 text-orange-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      {campaign.repost_verdict || 'N/A'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                                    {campaign.video_url ? (
                                      <a 
                                        href={campaign.video_url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-900"
                                      >
                                        🎥
                                      </a>
                                    ) : 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                                    <button
                                      onClick={() => {
                                        // You can add a modal or detailed view here
                                        console.log('View campaign details:', campaign);
                                      }}
                                      className="text-blue-600 hover:text-blue-900 font-medium"
                                    >
                                      View
                                    </button>
                                  </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                    </>
                  ) : selectedTeamMember ? (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                      <div className="text-center">
                        <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                                </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Campaigns Found</h3>
                        <p className="text-gray-500">No campaigns found for {selectedTeamMember} in the selected time period.</p>
                            </div>
                          </div>
                  ) : (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                      <div className="text-center">
                        <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                                </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Team Member</h3>
                        <p className="text-gray-500">Please select a team member to view their individual campaign details.</p>
                            </div>
                </div>
                  )}
                </>
              )}
            </>
          )}

          {/* No Data Message */}
          {teamPerformance.length === 0 && !isTeamLoading && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mt-2">No Team Data Available</h3>
                <p className="text-gray-500 mt-1">No team performance data found for the selected time period and report type.</p>
              </div>
            </div>
          )}
        </>
      )}


      {/* Engagement Report Tab Content */}
      {activeReportTab === 'engagement' && (
        <>
          {/* Engagement Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="space-y-4">
              {/* Period Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time Period</label>
                <select
                  value={engagementPeriodType}
                  onChange={(e) => setEngagementPeriodType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 min-w-[200px]"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="custom">Custom Date Range</option>
                </select>
              </div>

              {/* Date Range (only show for custom period) */}
              {engagementPeriodType === 'custom' && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                  <div className="flex items-center space-x-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                      <input
                        type="date"
                        value={engagementStartDate}
                        onChange={(e) => setEngagementStartDate(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                      <input
                        type="date"
                        value={engagementEndDate}
                        onChange={(e) => setEngagementEndDate(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setEngagementStartDate('');
                        setEngagementEndDate('');
                      }}
                      className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {isEngagementLoading ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Loading engagement analytics...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Engagement Overview Cards */}
              {engagementData.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                  <div className="stat-card">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-500">Total Engagement</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {engagementData.reduce((sum, item) => sum + (item.total_likes || 0) + (item.total_comments || 0) + (item.total_shares || 0) + (item.total_reposts || 0), 0).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-500">Total Likes</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {engagementData.reduce((sum, item) => sum + (item.total_likes || 0), 0).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-500">Total Comments</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {engagementData.reduce((sum, item) => sum + (item.total_comments || 0), 0).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-.316m0 0C11.186 3.9 9.59 2 7.5 2 5.418 2 3.9 3.8 2.5 6c0 0 3 2.5 7 2.5s7-2.5 7-2.5C13.9 3.8 12.382 2 10.3 2 8.21 2 6.614 3.9 5.5 6" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-500">Total Shares/Reposts</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {(engagementData.reduce((sum, item) => sum + (item.total_shares || 0), 0) + engagementData.reduce((sum, item) => sum + (item.total_reposts || 0), 0)).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Weekly Engagement Breakdown */}
              {engagementData.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    📈 Weekly Engagement Breakdown ({engagementPeriodType.charAt(0).toUpperCase() + engagementPeriodType.slice(1)})
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Week</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Likes</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Comments</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Shares</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Reposts</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Engagement</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Campaigns</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {engagementData.map((week, index) => (
                          <tr key={week.week_period || index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {week.week_period || `Week ${index + 1}`}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                              {formatNumber(week.total_likes || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                              {formatNumber(week.total_comments || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                              {formatNumber(week.total_shares || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                              {formatNumber(week.total_reposts || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {formatNumber((week.total_likes || 0) + (week.total_comments || 0) + (week.total_shares || 0) + (week.total_reposts || 0))}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                              {week.campaign_count || 0}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* No Data Message */}
              {engagementData.length === 0 && !isEngagementLoading && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                  <div className="text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mt-2">No Engagement Data Available</h3>
                    <p className="text-gray-500 mt-1">No engagement data found for the selected time period.</p>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ML Models Tab Content */}
      {activeReportTab === 'ml-models' && (
        <MLModels />
      )}
    </div>
  );
};

export default Reports;
